import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ComposableMap, Geographies, Geography, Graticule, Marker, Sphere } from 'react-simple-maps';
import './References.css';
import { COUNTRIES as countries } from '../data/countries';
import type { Country } from '../data/countries';

// Dünya haritası için topojson URL
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const RAD = Math.PI / 180;
// Degrees of spin per second while the globe idles.
const SPIN_SPEED = 6;

// Orthographic projection shows one hemisphere, so a marker on the far side must
// be culled — otherwise it bleeds through the globe. A point is visible when its
// angular distance from the projection centre is under 90°.
const isFacingCamera = (
  [lon, lat]: [number, number],
  [rotLon, rotLat]: [number, number]
): boolean => {
  const centreLon = -rotLon;
  const centreLat = -rotLat;
  const cosDistance =
    Math.sin(centreLat * RAD) * Math.sin(lat * RAD) +
    Math.cos(centreLat * RAD) * Math.cos(lat * RAD) * Math.cos((lon - centreLon) * RAD);
  return cosDistance > 0.06;
};


const References: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const handleGeographyClick = (geo: any) => {
    const countryCode = geo.properties.ISO_A3;
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setSelectedCountry(countryCode);
    }
  };

  const handleGeographyMouseEnter = (geo: any, event?: React.MouseEvent) => {
    const countryCode = geo.properties.ISO_A3;
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setTooltipContent(country.name);
      if (event) {
        setTooltipPosition({ x: event.clientX, y: event.clientY });
      }
    }
  };


  const handleGeographyMouseLeave = () => {
    setTooltipContent('');
    setTooltipPosition(null);
  };

  const handleMarkerMouseEnter = (country: Country, event: React.MouseEvent<SVGCircleElement>) => {
    setTooltipContent(country.name);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMarkerMouseLeave = () => {
    setTooltipContent('');
    setTooltipPosition(null);
  };

  // --- Globe rotation ---------------------------------------------------
  const [rotation, setRotation] = useState<[number, number]>([-20, -18]);
  const [isSpinning, setIsSpinning] = useState(true);
  const dragRef = useRef<{ x: number; y: number; lon: number; lat: number } | null>(null);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!isSpinning || prefersReducedMotion) return;
    let frame: number;
    let last = performance.now();
    const tick = (now: number) => {
      const elapsed = (now - last) / 1000;
      last = now;
      setRotation(([lon, lat]) => [lon - SPIN_SPEED * elapsed, lat]);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isSpinning, prefersReducedMotion]);

  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      lon: rotation[0],
      lat: rotation[1]
    };
    setIsSpinning(false);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    if (!start) return;
    const lon = start.lon + (event.clientX - start.x) * 0.25;
    // Clamp tilt so the globe never flips past the poles.
    const lat = Math.max(-80, Math.min(80, start.lat - (event.clientY - start.y) * 0.25));
    setRotation([lon, lat]);
  };

  const handleDragEnd = useCallback(() => {
    dragRef.current = null;
    setIsSpinning(true);
  }, []);

  return (
    <div className="references-page">
      {/* Hero Section */}
      <section className="articles-hero">
        <div className="articles-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="articles-hero-text"
          >
            <h1>{t('references.title')}</h1>
            <p className="articles-hero-subtitle">
              {t('references.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="page-container">
        <section className="references-intro">
          <div className="references-description">
            <p>
              {t('references.description.paragraph1')}
            </p>
            <p>
              {t('references.description.paragraph2')}
            </p>
            <p>
              {t('references.description.paragraph3')}
            </p>
            <p>
              {t('references.description.paragraph4')}
            </p>
            <p>
              {t('references.description.paragraph5')}
            </p>
          </div>
        </section>
        <section className="world-map-section">
          <h2>{t('references.map.title')}</h2>
          <p className="map-description">
            {t('references.map.description')}
          </p>
          <div
            className="map-container globe-container"
            onMouseMove={(e) => {
              if (tooltipContent && tooltipPosition) {
                setTooltipPosition({ x: e.clientX, y: e.clientY });
              }
            }}
            onMouseLeave={handleGeographyMouseLeave}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
          >
            <ComposableMap
              projection="geoOrthographic"
              projectionConfig={{
                scale: 260,
                rotate: [rotation[0], rotation[1], 0]
              }}
              width={548}
              height={548}
              style={{ width: '100%', height: 'auto', touchAction: 'none' }}
            >
              <defs>
                <radialGradient id="globe-ocean" cx="35%" cy="30%" r="80%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#e9edf3" />
                </radialGradient>
                {/* Terminator shading gives the sphere its roundness. */}
                <radialGradient id="globe-shade" cx="35%" cy="30%" r="78%">
                  <stop offset="55%" stopColor="#7d8899" stopOpacity="0" />
                  <stop offset="100%" stopColor="#7d8899" stopOpacity="0.32" />
                </radialGradient>
              </defs>

              <Sphere id="globe-sphere" fill="url(#globe-ocean)" stroke="none" strokeWidth={0} />
              <Graticule stroke="rgba(20,30,50,0.09)" strokeWidth={0.5} />

              <Geographies geography={geoUrl}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const countryCode = geo.properties.ISO_A3;
                    const isActive = countries.some(c => c.code === countryCode);
                    const isSelected = selectedCountry === countryCode;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isSelected ? 'var(--brand-700)' : isActive ? 'var(--brand)' : '#c3cbd6'}
                        stroke={isActive ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)'}
                        strokeWidth={isActive ? 0.5 : 0.35}
                        style={{
                          default: {
                            outline: 'none',
                            cursor: isActive ? 'pointer' : 'default'
                          },
                          hover: {
                            fill: isActive ? 'var(--brand-700)' : '#aeb8c6',
                            outline: 'none'
                          },
                          pressed: {
                            fill: isActive ? 'var(--brand-700)' : '#aeb8c6',
                            outline: 'none'
                          }
                        }}
                        onClick={() => handleGeographyClick(geo)}
                        onMouseEnter={() => handleGeographyMouseEnter(geo)}
                        onMouseLeave={handleGeographyMouseLeave}
                      />
                    );
                  })
                }
              </Geographies>

              {countries
                .filter((country) => isFacingCamera(country.coordinates, rotation))
                .map((country) => (
                  <Marker key={country.code} coordinates={country.coordinates}>
                    <circle r={7} fill="var(--brand)" opacity={0.28} />
                    <circle
                      r={3.5}
                      fill="#ffffff"
                      stroke="var(--brand)"
                      strokeWidth={1.5}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => handleMarkerMouseEnter(country, e)}
                      onMouseLeave={handleMarkerMouseLeave}
                      onMouseMove={(e) => {
                        if (tooltipContent === country.name) {
                          setTooltipPosition({ x: e.clientX, y: e.clientY });
                        }
                      }}
                    />
                  </Marker>
                ))}

              {/* Painted last so the shading sits above the landmasses. */}
              <Sphere
                id="globe-shading"
                fill="url(#globe-shade)"
                stroke="rgba(20,30,50,0.16)"
                strokeWidth={0.8}
                style={{ pointerEvents: 'none' }}
              />
            </ComposableMap>
            {tooltipContent && tooltipPosition && (
              <div
                className="map-tooltip"
                style={{
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`
                }}
              >
                {tooltipContent}
              </div>
            )}
          </div>
          <div className="map-legend">
            <div className="legend-item">
              <span className="legend-color active"></span>
              <span>{t('references.map.legend.active')} ({countries.length})</span>
            </div>
            <div className="legend-item">
              <span className="legend-color inactive"></span>
              <span>{t('references.map.legend.inactive')}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default References;
